/**
 * BannerCarouselDynamic — iter101.
 *
 * Thin wrapper around ``<BannerCarousel />`` that fetches banners on
 * mount from ``/api/banners`` scoped to a placement + optional scope
 * ids. Renders nothing when the response is empty (silent no-op) so
 * pages can drop it in unconditionally without worrying about layout
 * gaps.
 *
 * Usage:
 *   <BannerCarouselDynamic placement="VERTICAL" businessCategoryId={id} />
 *   <BannerCarouselDynamic placement="SHOP" restaurantId={rid} />
 *
 * We deliberately do NOT cache across mounts — the banner list is
 * small (≤20 rows) and admin edits should reflect on next navigate.
 */
import { useEffect, useState } from "react";
import BannerCarousel from "./BannerCarousel";
import { api } from "../lib/api";

export default function BannerCarouselDynamic({
    placement,
    businessCategoryId,
    restaurantId,
    className = "",
}) {
    const [banners, setBanners] = useState([]);
    useEffect(() => {
        let cancelled = false;
        const params = new URLSearchParams({ placement });
        if (businessCategoryId) params.set("business_category_id", businessCategoryId);
        if (restaurantId) params.set("restaurant_id", restaurantId);
        api.get(`/banners?${params.toString()}`)
            .then(({ data }) => { if (!cancelled) setBanners(data?.banners || []); })
            .catch(() => { if (!cancelled) setBanners([]); });
        return () => { cancelled = true; };
    }, [placement, businessCategoryId, restaurantId]);

    if (!banners.length) return null;
    return (
        <div
            data-testid={`banners-${placement.toLowerCase()}`}
            className={className}
        >
            <BannerCarousel banners={banners} />
        </div>
    );
}
